import React, { useState, useContext, useEffect } from 'react';
import { injectIntl, useIntl } from 'react-intl';
import {
  getTranslatedText,
  ALL_TABLES,
  saveTableRowCount,
  getAllColumnWidth,
  saveColumnWidth,
  SkeletonTableLoading,
  getPersonalizedSorting,
  saveSortingPersonalization,
  getCurrentUserHook,
  SuperList,
  useSuperListHook
} from '@nile/components';
import { useUserClientCheck } from '@nile/components/src/Utils/helpers';
import envConfig from '@nile/home/src/Config/env';
import {
  RMMClient,
  AccountClient,
  GET_PERSONALIZATION_BY_NAME,
  GET_ENTITY_NAMES_META
} from '@nile/graphql';
import RMMContext from '@nile/rmm/src/modules/common/contexts/RMMContext';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import { ALL_ASSET_TYPES, getAssetsEntityType } from '@nile/rmm/src/constants';
import { getProcessedConfig } from '@nile/rmm/src/modules/common/services/tableHelper';
import {
  GET_MONITORED_ASSET_META_INFO,
  GET_ALL_ASSETS
} from '@nile/rmm/src/modules/asset-management/Graphql/asset';

import {
  constructTableData,
  columnsConfig,
  getEntityVariables,
  getMonitoredAssetsQuery,
  getNetworkEntityVariables
} from './all-monitored-assets-view-helper';
import { getAttributeName } from './all-monitored-assets-view-filter-helper';
import { useParams } from 'react-router-dom';

interface AllMonitoredAssetViewProps {
  intl: any;
  handleFilterChange: Function;
  listInfo: any;
  isViewChange: boolean;
  viewSubType?: string;
  activeView: any;
  padded?: string;
  checkBoxSize?: string;
  defaultFilters?: any;
  defaultColumnsToExclude?: string[];
  isClientDetailsPage: boolean;
  noBulkSelect?: boolean;
  isPolicyCategory?: boolean;
  assetClassId?: string;
  supportedModules?: string[];
  reRenderOnDefaultFilterChange: boolean;
  source?: string;
  tableName?: string;
  tab?: string;
}

function AllMonitoredAssetView({
  activeView,
  viewSubType,
  padded = 'sm',
  checkBoxSize = 'md',
  defaultFilters = null,
  defaultColumnsToExclude = [],
  isClientDetailsPage = false,
  noBulkSelect = false,
  isPolicyCategory = false,
  assetClassId,
  supportedModules,
  reRenderOnDefaultFilterChange = false,
  source,
  tableName = ALL_TABLES.allMonitorAssetTable,
  tab
}: AllMonitoredAssetViewProps) {
  const intl = useIntl();
  const viewId = tab;
  const rmmContext = useContext(RMMContext);
  const tabContext = useContext(TicketContext);
  const user = getCurrentUserHook();
  const columnWidthObject = getAllColumnWidth(tableName);
  const isAllAssets =
    viewSubType === ALL_ASSET_TYPES.ALL_ASSETS ||
    viewSubType === ALL_ASSET_TYPES.ALL_NON_MONITORED_ASSET;
  const [columns, setColumns] = useState(null);
  const [tableBodyLoader, setTableBodyLoader] = useState(true);
  const [printerAssets, setPrinterAssets] = useState([]);
  const isAssetInClientPortal = useUserClientCheck();
  const personalizedSorting = getPersonalizedSorting(tableName, viewId);

  const {
    tableCondition,
    advanceFilterCondition,
    updateTableFilterCondition,
    records,
    setRecords
  } = useSuperListHook({
    tableName,
    viewId: viewId
  });

  const fetchEntityMeta = async () => {
    const response = await RMMClient.query({
      query: GET_ENTITY_NAMES_META,
      variables: {
        entitiesName: activeView?.entityName
          ? getNetworkEntityVariables(
              viewSubType || ALL_ASSET_TYPES.ALL_MONITORED_ASSET,
              activeView?.entityName
            )
          : getEntityVariables(
              viewSubType || ALL_ASSET_TYPES.ALL_MONITORED_ASSET,
              activeView?.entityName
            )
      }
    });
    return response.data;
  };

  const fetchAllMonitoredAssetMeta = async () => {
    const response = await RMMClient.query({
      query: GET_MONITORED_ASSET_META_INFO,
      variables: {
        ...(!isPolicyCategory && {
          viewType: viewSubType
        }),
        version: 1,
        ...(isPolicyCategory && {
          assetClassId
        })
      },
      fetchPolicy: 'network-only'
    });

    return response.data;
  };

  const getCurrentViewName = () => {
    if (isClientDetailsPage) {
      return source === 'DEVICE_CATEGORY'
        ? 'ClientDetailsDeviceCategoryAssetView'
        : 'ClientDetailsAssetView';
    }
    switch (source) {
      case 'DEVICE_CATEGORY':
        return 'DeviceCategory';
      case 'ASSET_GROUP':
        return 'AssetGroup';
      default:
        return isAssetInClientPortal ? 'PortalAssetView' : 'AssetView';
    }
  };

  const fetchPersonalization = async () => {
    const response = await AccountClient.query({
      query: GET_PERSONALIZATION_BY_NAME,
      fetchPolicy: 'network-only',
      variables: {
        userId: user?.userId,
        category: getCurrentViewName(),
        name: viewId
      }
    });
    return response.data.getPersonalizationByCategoryAndName;
  };

  const columnAction = (args: string, data: any = {}) => {
    switch (args) {
      case 'detailsView':
        navToDetailsPage(data);
        break;
      default:
        setTableBodyLoader(true);
        generateColumns();
        break;
    }
  };
  const generateColumns = async () => {
    setTableBodyLoader(true);
    const entityMeta = await fetchEntityMeta();
    const allMonitoredAssetMeta = await fetchAllMonitoredAssetMeta();
    const personalization = await fetchPersonalization();
    const customColumns = personalization?.value
      ? JSON.parse(personalization?.value)
      : [];
    const { configuredCols, allCols } = getProcessedConfig({
      entitiesMeta: entityMeta?.getEntitiesMeta,
      allColumns:
        allMonitoredAssetMeta.getMonitoredAssetsMetaInfo.selectAttributes,
      intl,
      module: activeView.entityName
        ? activeView.entityName
        : getAssetsEntityType(viewSubType),
      columnPersonalization: customColumns,
      searchAttributes:
        allMonitoredAssetMeta.getMonitoredAssetsMetaInfo.searchAttributes,
      defaultColumns:
        allMonitoredAssetMeta.getMonitoredAssetsMetaInfo
          .defaultSelectedAttributes,
      defaultColumnsToExclude: defaultColumnsToExclude
    });
    // Set column for column customization
    if (rmmContext !== 'rmm') {
      rmmContext.setColumnConfigurations({
        allColumns: [
          {
            name: 'columns',
            header: 'Columns',
            items: allCols
          }
        ],
        configuredColumns: configuredCols
      });
    }
    if (
      activeView?.systemViewProperty === ALL_ASSET_TYPES.ALL_UNLICENSED_ASSET
    ) {
      configuredCols.push({
        label: '\u0020',
        name: 'assetActivate'
      });
    }

    const restrictFilter = rmmContext?.restrictFilter;

    const { columnsGroup } = constructTableData(
      configuredCols,
      columnWidthObject,
      allMonitoredAssetMeta.getMonitoredAssetsMetaInfo.searchAttributes,
      allMonitoredAssetMeta.getMonitoredAssetsMetaInfo.sortAttributes,
      columnAction,
      updateTableFilterCondition,
      getAssetsEntityType(viewSubType), //Module name
      viewSubType,
      activeView,
      isAssetInClientPortal,
      restrictFilter
    );
    setColumns(columnsGroup[0].columns);
    setTableBodyLoader(false);
  };

  const navToDetailsPage = (obj) => {
    const { assetId, name, assetClass } = obj?.row?.original;
    tabContext.openTab({
      module: 'ALL_ASSET_DETAILS',
      data: {
        id: assetId,
        subject: name,
        classId: assetClass?.classId
      }
    });
  };

  useEffect(() => {
    generateColumns();
  }, [viewId, rmmContext?.updatedConfiguredColumns]);

  return (
    <>
      {tableBodyLoader ? (
        <SkeletonTableLoading />
      ) : (
        <SuperList
          noBulkSelect={isAssetInClientPortal || noBulkSelect}
          fullHeight
          isLoading={tableBodyLoader}
          columns={columns}
          defaultColumnCells={columnsConfig}
          data={printerAssets}
          setData={setPrinterAssets}
          emptyState={{
            nodataImage: `${envConfig.imageRoot}svg/not-found.svg`,
            heading: getTranslatedText(intl, 'app.selectionsheet.emptyState'),
            description: ''
          }}
          tableCondition={tableCondition}
          advanceFilterCondition={advanceFilterCondition}
          query={
            isAllAssets
              ? GET_ALL_ASSETS
              : getMonitoredAssetsQuery(supportedModules, [{ columns }])
          }
          client={RMMClient}
          records={records}
          setRecords={setRecords}
          accessorKey={isAllAssets ? 'getAllAssets' : 'getMonitoredAssets'}
          dataKey="assets"
          tableName={tableName}
          saveColumnWidth={saveColumnWidth}
          saveTableRowCount={saveTableRowCount}
          user={user}
          getAttributeName={getAttributeName}
          columnWidthPersonalization
          ldFlagEnabled
          padded={padded}
          checkBoxSize={checkBoxSize}
          sortingPersonalization
          saveSortingPersonalization={saveSortingPersonalization}
          viewId={viewId}
          defaultSort={personalizedSorting}
          appendViewIdToListInfo
          additionalVariables={{
            ...(!isPolicyCategory && {
              viewType: viewSubType
            }),
            ...(isPolicyCategory && {
              assetClassId
            })
          }}
          defaultCondition={defaultFilters}
          enableSelectAll
          maintainSelection
          idKey="assetId"
        />
      )}
    </>
  );
}

export default injectIntl(AllMonitoredAssetView);
